import 'react-image-crop/dist/ReactCrop.css';
import 'react-select-me/lib/ReactSelectMe.css';

import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import type { Crop } from 'react-image-crop';
import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop';
import { useSelector } from 'react-redux';
import SelectMe from 'react-select-me';

import type { ReferentMutation } from '../../../../@types/Company';
import type { User } from '../../../../@types/User';
import type { RootState } from '../../../../store/store';
import { getSalaries } from '../../../../utils/api';
import { JWTAuthorization, JWTHeader } from '../../../../utils/JWTHeader';
import { extractUniqueUsersFromFonctionResults } from '../../../../utils/users';
import FileSelector from '../../../common/FileSelector/FileSelector';
import { showToast } from '../../../common/Notification';
import Spinner from '../../../common/Spinner';

const generateReferentId = () => `ref_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

const createEmptyReferent = (): ReferentMutation => ({
  id: generateReferentId(),
  title: '',
  firstname: '',
  lastname: '',
  email: '',
  phone: '',
  photoName: undefined,
  hasExternalNewsletter: false,
  externalNewsletterUrl: '',
  hasRgpdBlogArticle: false,
  rgpdBlogArticleUrl: '',
  userId: null,
});

export interface ReferentStepHandle {
  isValid: () => boolean;
  getPartialDraft: () => { referents: ReferentMutation[] };
  focusFirstInvalid: () => void;
}

interface ReferentStepProps {
  initial: { referents: ReferentMutation[] };
  onChange?: (partial: { referents: ReferentMutation[] }) => void;
}

const ReferentStep = forwardRef<ReferentStepHandle, ReferentStepProps>(({ initial, onChange }, ref) => {
  const [referents, setReferents] = useState<ReferentMutation[]>(
    () => initial.referents.length > 0 ? initial.referents : [createEmptyReferent()],
  );
  const firstNameRefs = useRef<Map<string, HTMLInputElement | null>>(new Map());
  const company = useSelector((s: RootState) => s.auth.company);
  const jwt = useSelector((s: RootState) => s.auth.token);
  const [savingPhotoForId, setSavingPhotoForId] = useState<string | null>(null);
  const [companyUsers, setCompanyUsers] = useState<User[]>([]);

  useEffect(() => {
    if (!company?.id || !jwt) { return; }
    fetch(getSalaries(company.id, false), JWTHeader(jwt))
      .then((res) => res.json())
      .then((data) => {
        if (data?.results) {
          setCompanyUsers(
            extractUniqueUsersFromFonctionResults(
              data.results,
              (fonctionResult) => fonctionResult.company?.id === company.id,
            ),
          );
        }
      })
      .catch(console.error);
  }, [company?.id, jwt]);

  const userOptions = useMemo(() => {
    const sorted = [...companyUsers].sort((a, b) => (a.lastname ?? '').localeCompare(b.lastname ?? ''));
    return [
      { value: null as number | null, label: '-- Aucun --' },
      ...sorted.map((user) => ({
        value: user.id as number | null,
        label: `${user.firstname} ${user.lastname ? user.lastname.toUpperCase() : ''}`,
      })),
    ];
  }, [companyUsers]);

  // Cropper state for each referent
  const [showCropper, setShowCropper] = useState(false);
  const [cropImage, setCropImage] = useState<string | null>(null);
  const [crop, setCrop] = useState<Crop | undefined>();
  const [completedCrop, setCompletedCrop] = useState<Crop | null>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const [tempFile, setTempFile] = useState<File | null>(null);
  const [currentCropReferentId, setCurrentCropReferentId] = useState<string | null>(null);

  const updateReferent = useCallback((id: string, updates: Partial<ReferentMutation>) => {
    const updated = referents.map(r => r.id === id ? { ...r, ...updates } : r);
    setReferents(updated);
    onChange?.({ referents: updated });
  }, [referents, onChange]);

  const addReferent = useCallback(() => {
    const updated = [...referents, createEmptyReferent()];
    setReferents(updated);
    onChange?.({ referents: updated });
  }, [referents, onChange]);

  const removeReferent = useCallback((id: string) => {
    if (referents.length <= 1) {
      showToast('warning', 'Vous devez avoir au moins un référent.');
      return;
    }
    const updated = referents.filter(r => r.id !== id);
    setReferents(updated);
    onChange?.({ referents: updated });
  }, [referents, onChange]);

  const isValid = useCallback(() => {
    // At least one referent must be complete (title, firstname, lastname)
    return referents.some(r => 
      r.title.trim().length > 0 && 
      r.firstname.trim().length > 0 && 
      r.lastname.trim().length > 0,
    );
  }, [referents]);

  const focusFirstInvalid = useCallback(() => {
    const firstInvalid = referents.find(r => 
      r.title.trim().length === 0 || 
      r.firstname.trim().length === 0 || 
      r.lastname.trim().length === 0,
    );
    if (firstInvalid) {
      const input = firstNameRefs.current.get(firstInvalid.id);
      if (input) {
        input.focus();
      }
    }
  }, [referents]);

  useImperativeHandle(ref, () => ({
    isValid,
    focusFirstInvalid,
    getPartialDraft: () => ({ referents }),
  }), [referents, isValid, focusFirstInvalid]);

  const centerAspectCrop = (mediaWidth: number, mediaHeight: number, aspect: number) => {
    return centerCrop(
      makeAspectCrop(
        { unit: '%', width: 90 },
        aspect,
        mediaWidth,
        mediaHeight,
      ),
      mediaWidth,
      mediaHeight,
    );
  };

  const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget;
    setCrop(centerAspectCrop(width, height, 1));
  };

  const handleReferentPhotoUpload = async(referentId: string, event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {return;}
    const imageUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = async() => {
      const isSquare = Math.abs(img.width - img.height) <= 2;
      if (isSquare) {
        if (!company?.id) { return; }
        setSavingPhotoForId(referentId);
        try {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('referentId', referentId);
          const response = await fetch(`/api/companies/${company.id}/referent-photo`, {
            method: 'POST',
            headers: { Authorization: JWTAuthorization(jwt) },
            body: formData,
          });
          if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error((errorData && errorData.errors && errorData.errors.file) || 'Échec du téléchargement');
          }
          const data = await response.json();
          // Update the referent's photoName in state
          updateReferent(referentId, { photoName: data.referentPhotoName });
          showToast('success', 'Photo référent enregistrée.');
          URL.revokeObjectURL(imageUrl);
        } catch (err) {
          console.error(err);
          showToast('error', 'Échec du téléchargement de la photo');
          URL.revokeObjectURL(imageUrl);
        } finally {
          setSavingPhotoForId(null);
        }
      } else {
        setTempFile(file);
        setCropImage(imageUrl);
        setCurrentCropReferentId(referentId);
        setShowCropper(true);
      }
    };
    img.onerror = () => {
      setTempFile(file);
      setCropImage(imageUrl);
      setCurrentCropReferentId(referentId);
      setShowCropper(true);
      showToast('warning', 'Dimensions inconnues. Recadrage requis.');
    };
    img.src = imageUrl;
  };

  const handleCropComplete = useCallback(async() => {
    if (!completedCrop || !imgRef.current || !tempFile || !company?.id || !currentCropReferentId) { return; }
    const canvas = document.createElement('canvas');
    const scaleX = imgRef.current.naturalWidth / imgRef.current.width;
    const scaleY = imgRef.current.naturalHeight / imgRef.current.height;
    const pixelRatio = window.devicePixelRatio;
    canvas.width = completedCrop.width * scaleX * pixelRatio;
    canvas.height = completedCrop.height * scaleY * pixelRatio;
    const ctx = canvas.getContext('2d');
    if (!ctx) { return; }
    ctx.scale(pixelRatio, pixelRatio);
    ctx.imageSmoothingQuality = 'high';
    const cropX = completedCrop.x * scaleX;
    const cropY = completedCrop.y * scaleY;
    const cropWidth = completedCrop.width * scaleX;
    const cropHeight = completedCrop.height * scaleY;
    ctx.drawImage(imgRef.current, cropX, cropY, cropWidth, cropHeight, 0, 0, cropWidth, cropHeight);
    canvas.toBlob(async(blob) => {
      if (!blob) { showToast('error', 'Erreur lors du recadrage'); return; }
      const croppedFile = new File([blob], tempFile.name, { type: tempFile.type, lastModified: Date.now() });
      setSavingPhotoForId(currentCropReferentId);
      try {
        const formData = new FormData();
        formData.append('file', croppedFile);
        formData.append('referentId', currentCropReferentId);
        const response = await fetch(`/api/companies/${company.id}/referent-photo`, {
          method: 'POST',
          headers: { Authorization: JWTAuthorization(jwt) },
          body: formData,
        });
        if (!response.ok) {
          const errorData = await response.json().catch(() => null);
          throw new Error((errorData && errorData.errors && errorData.errors.file) || 'Échec du téléchargement');
        }
        const data = await response.json();
        // Update the referent's photoName in state
        updateReferent(currentCropReferentId, { photoName: data.referentPhotoName });
        showToast('success', 'Photo référent enregistrée.');
      } catch (err) {
        console.error(err);
        showToast('error', 'Échec du téléchargement de la photo');
      } finally {
        setSavingPhotoForId(null);
        setShowCropper(false);
        if (cropImage) { URL.revokeObjectURL(cropImage); }
        setCropImage(null);
        setTempFile(null);
        setCurrentCropReferentId(null);
      }
    }, tempFile.type);
  }, [completedCrop, tempFile, company?.id, jwt, cropImage, currentCropReferentId, updateReferent]);

  const handleCancelCrop = () => {
    setShowCropper(false);
    if (cropImage) { URL.revokeObjectURL(cropImage); }
    setCropImage(null);
    setTempFile(null);
    setCurrentCropReferentId(null);
  };

  const isReferentValid = (referent: ReferentMutation) => {
    return referent.title.trim().length > 0 && 
           referent.firstname.trim().length > 0 && 
           referent.lastname.trim().length > 0;
  };

  return (
    <div>
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-800">Référents mutation/location</h3>
      </div>
      <p className="mb-6 text-sm text-gray-500">
        Configurez vos référents mutations/location. Ces informations s'afficheront dans vos communications.
        Un PDF sera généré pour chaque référent lors de l'aperçu de la newsletter.
      </p>

      <div className="space-y-6">
        {referents.map((referent, index) => (
          <div 
            key={referent.id} 
            className={`rounded-lg border p-4 ${isReferentValid(referent) ? 'border-green-300 bg-green-50/30' : 'border-gray-200 bg-white'}`}
          >
            <div className="mb-4 flex items-center justify-between">
              <h4 className="text-md font-medium text-gray-700">
                Référent {index + 1}
                {isReferentValid(referent) && (
                  <span className="ml-2 text-sm text-green-600" role="img" aria-label="Complet">✓ Complet</span>
                )}
              </h4>
              {referents.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeReferent(referent.id)}
                  className="flex items-center gap-1 rounded px-2 py-1 text-sm text-red-600 transition-colors hover:bg-red-50 hover:text-red-700"
                >
                  <TrashIcon className="h-4 w-4" />
                  Supprimer
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Civilité *</label>
                <select 
                  value={referent.title} 
                  onChange={(e) => updateReferent(referent.id, { title: e.target.value })} 
                  className={`w-full rounded-lg border p-2.5 ${referent.title.trim().length === 0 ? 'border-red-400' : 'border-gray-300'}`}
                >
                  <option value="">Sélectionner</option>
                  <option value="Monsieur">Monsieur</option>
                  <option value="Madame">Madame</option>
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Prénom *</label>
                <input 
                  ref={(el) => { firstNameRefs.current.set(referent.id, el); }}
                  type="text" 
                  value={referent.firstname} 
                  onChange={(e) => updateReferent(referent.id, { firstname: e.target.value })} 
                  className={`w-full rounded-lg border p-2.5 ${referent.firstname.trim().length === 0 ? 'border-red-400' : 'border-gray-300'}`} 
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Nom *</label>
                <input 
                  type="text" 
                  value={referent.lastname} 
                  onChange={(e) => updateReferent(referent.id, { lastname: e.target.value })} 
                  className={`w-full rounded-lg border p-2.5 ${referent.lastname.trim().length === 0 ? 'border-red-400' : 'border-gray-300'}`} 
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Email</label>
                <input
                  type="email"
                  value={referent.email || ''}
                  onChange={(e) => updateReferent(referent.id, { email: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 p-2.5"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Téléphone</label>
                <input
                  type="tel"
                  value={referent.phone || ''}
                  onChange={(e) => updateReferent(referent.id, { phone: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 p-2.5"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Utilisateur correspondant</label>
                <SelectMe
                  options={userOptions}
                  value={referent.userId != null
                    ? userOptions.find((opt) => opt.value === referent.userId)
                    : { value: null, label: '-- Aucun --' }}
                  onChange={(selected) => {
                    const option = Array.isArray(selected) ? selected[0] : selected;
                    updateReferent(referent.id, { userId: option?.value ?? null });
                  }}
                  searchable
                  placeholder="Rechercher un utilisateur..."
                />
              </div>
            </div>

            <div className="mt-4">
              <label className="mb-2 block text-sm font-medium text-gray-700">Photo du référent</label>
              <div className="flex items-center gap-4">
                {referent.photoName && (
                  <div className="flex flex-col items-center gap-2">
                    <img 
                      src={referent.photoName} 
                      alt={`Photo de ${referent.firstname} ${referent.lastname}`} 
                      className="h-20 w-20 rounded-full border object-cover" 
                    />
                    <p className="text-xs text-gray-500">Photo actuelle</p>
                  </div>
                )}
                <div className="flex-1">
                  <FileSelector
                    accept="image/jpeg, image/png"
                    onChange={(e) => handleReferentPhotoUpload(referent.id, e)}
                    disabled={savingPhotoForId === referent.id}
                  />
                  {savingPhotoForId === referent.id && (
                    <div className="mt-2 flex items-center text-sm text-gray-500">
                      <Spinner size="sm" className="mr-2" />Téléversement…
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* External Newsletter Section */}
            <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50 p-4">
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id={`external-newsletter-${referent.id}`}
                  checked={referent.hasExternalNewsletter || false}
                  onChange={(e) => updateReferent(referent.id, { 
                    hasExternalNewsletter: e.target.checked,
                    externalNewsletterUrl: e.target.checked ? referent.externalNewsletterUrl : '',
                  })}
                  className="mt-1 h-4 w-4 rounded border-gray-300 text-rgpd-blue focus:ring-rgpd-blue"
                />
                <div className="flex-1">
                  <label 
                    htmlFor={`external-newsletter-${referent.id}`} 
                    className="block cursor-pointer text-sm font-medium text-gray-700"
                  >
                    Ce référent a une Newsletter au sein de la société sur un site web conforme
                  </label>
                  <p className="mt-1 text-xs text-gray-500">
                    L'URL fournie sera utilisée pour générer les QR codes dans les PDFs à la place du blog.
                  </p>
                  
                  {referent.hasExternalNewsletter && (
                    <div className="mt-3">
                      <label className="mb-1 block text-xs font-medium text-gray-600">
                        URL de la Newsletter externe
                      </label>
                      <input
                        type="url"
                        value={referent.externalNewsletterUrl || ''}
                        onChange={(e) => updateReferent(referent.id, { externalNewsletterUrl: e.target.value })}
                        placeholder="https://exemple.com/newsletter"
                        className="w-full rounded-lg border border-gray-300 p-2.5 text-sm focus:border-transparent focus:ring-2 focus:ring-rgpd-blue"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* RGPD Blog Article Section */}
            <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50 p-4">
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id={`rgpd-blog-article-${referent.id}`}
                  checked={referent.hasRgpdBlogArticle || false}
                  onChange={(e) => updateReferent(referent.id, {
                    hasRgpdBlogArticle: e.target.checked,
                    rgpdBlogArticleUrl: e.target.checked ? referent.rgpdBlogArticleUrl : '',
                  })}
                  className="mt-1 h-4 w-4 rounded border-gray-300 text-rgpd-blue focus:ring-rgpd-blue"
                />
                <div className="flex-1">
                  <label
                    htmlFor={`rgpd-blog-article-${referent.id}`}
                    className="block cursor-pointer text-sm font-medium text-gray-700"
                  >
                    Ce référent dispose d'un Article de blog dédié justifiant la surveillance des DPEs :
                  </label>
                  <p className="mt-1 text-xs text-gray-500">
                    L'URL fournie remplacera le lien généré vers la page newsletter-dpe dans les courriers et emails de DPE.
                  </p>

                  {referent.hasRgpdBlogArticle && (
                    <div className="mt-3">
                      <label className="mb-1 block text-xs font-medium text-gray-600">
                        URL de l'article de blog
                      </label>
                      <input
                        type="url"
                        value={referent.rgpdBlogArticleUrl || ''}
                        onChange={(e) => updateReferent(referent.id, { rgpdBlogArticleUrl: e.target.value })}
                        placeholder="https://exemple.com/blog/rgpd"
                        className="w-full rounded-lg border border-gray-300 p-2.5 text-sm focus:border-transparent focus:ring-2 focus:ring-rgpd-blue"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 flex justify-center">
        <button
          type="button"
          onClick={addReferent}
          className="flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2.5 text-base text-white transition-colors hover:bg-green-700"
        >
          <PlusIcon className="h-5 w-5" />
          Ajouter un référent
        </button>
      </div>

      {showCropper && cropImage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-2xl rounded-lg bg-white p-6 shadow-xl">
            <h4 className="mb-4 text-lg font-semibold">Recadrer la photo</h4>
            <div className="mb-4 max-h-[60vh] overflow-auto">
              <ReactCrop crop={crop} onChange={(c) => setCrop(c)} onComplete={(c) => setCompletedCrop(c)} aspect={1}>
                <img ref={imgRef} src={cropImage} alt="À recadrer" onLoad={onImageLoad} className="max-w-full" />
              </ReactCrop>
            </div>
            <div className="flex justify-end gap-3">
              <button type="button" onClick={handleCancelCrop} className="rounded-lg border border-gray-300 px-4 py-2 hover:bg-gray-50">Annuler</button>
              <button type="button" onClick={handleCropComplete} disabled={!completedCrop} className="rounded-lg bg-rgpd-blue px-4 py-2 text-white disabled:opacity-50">Appliquer le recadrage</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

ReferentStep.displayName = 'ReferentStep';

export default ReferentStep;
